/**
 * Store del audit trail — encadenado con hash chain.
 *
 * Persiste a localStorage. Verifica integridad al hidratar y guarda el
 * resultado en `integridad` (la UI puede mostrar un banner si está rota).
 *
 * Cada acción crítica del sistema llama a `addEntry()` con el input parcial;
 * el store completa el resto (usuario activo, timestamps, hashes) usando
 * `buildAuditEntry` del domain.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  buildAuditEntry,
  verificarIntegridadAuditTrail,
  type IntegridadResult,
} from '@/domain/audit';
import type { AuditEntry, AuditEntryInput } from '@/domain/types';
import { useSessionStore } from './session';

interface AuditState {
  entries: AuditEntry[];
  counter: number;
  integridad: IntegridadResult | null;

  addEntry: (input: AuditEntryInput) => AuditEntry;
  reverificarIntegridad: () => IntegridadResult;
  clear: () => void;
}

export const useAuditStore = create<AuditState>()(
  persist(
    (set, get) => ({
      entries: [],
      counter: 0,
      integridad: null,

      addEntry: (input) => {
        const session = useSessionStore.getState();
        const next = get().counter + 1;
        const prev = get().entries[get().entries.length - 1] ?? null;
        const entry = buildAuditEntry(input, {
          usuario: session.usuario,
          sesionLoginAt: session.loginAt,
          prevEntry: prev,
          nextCounter: next,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 100) : null,
        });
        const newEntries = [...get().entries, entry];
        const integridad = verificarIntegridadAuditTrail(newEntries);
        set({ entries: newEntries, counter: next, integridad });
        return entry;
      },

      reverificarIntegridad: () => {
        const integridad = verificarIntegridadAuditTrail(get().entries);
        set({ integridad });
        return integridad;
      },

      clear: () => {
        set({ entries: [], counter: 0, integridad: null });
      },
    }),
    {
      name: 'riga-traza-audit-v1',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ entries: state.entries, counter: state.counter }),
      // Al rehidratar, verificar integridad (detecta tampering del localStorage)
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.integridad = verificarIntegridadAuditTrail(state.entries);
        }
      },
    },
  ),
);
